/**
 * DownloadPolicyDialog — Shows a policy acknowledgement dialog before any
 * report / file download.  Wraps any download trigger so every export across
 * the app goes through a single, consistent policy gate.
 *
 * ── Usage (wrapper pattern) ──────────────────────────────────────────────
 *
 *   import { DownloadWithPolicy } from '../common/DownloadPolicyDialog'
 *
 *   <DownloadWithPolicy onConfirm={() => downloadFile(data, 'report.csv')}>
 *     <Button variant='outlined'>Export CSV</Button>
 *   </DownloadWithPolicy>
 *
 * ── Usage (hook pattern) ─────────────────────────────────────────────────
 *
 *   import { useDownloadWithPolicy } from '../common/DownloadPolicyDialog'
 *
 *   const { triggerDownload, PolicyDialog } = useDownloadWithPolicy()
 *
 *   <Button onClick={() => triggerDownload(() => downloadFile(data, 'r.csv'))}>
 *     Export
 *   </Button>
 *   {PolicyDialog}
 */
import React, { useState, useCallback, useRef } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import palette from '../../theme/palette'
import { post } from './constants'
import { getUserName } from '../helpers/ReduxHelpers'

/* ── Audit helper (best-effort, never blocks download) ─────────── */
function auditDownload(reportName) {
  try {
    const payload = {
      action: 'REPORT_DOWNLOAD',
      user: getUserName(),
      reportName: reportName || 'unknown',
      timestamp: new Date().toISOString(),
    }
    // Fire-and-forget — don't block the download
    post('api/audit/log', payload).catch(() => {})
  } catch (_) {
    // audit failure must never prevent download
  }
}

/* ── Wrapper component ─────────────────────────────────────────── */

export function DownloadWithPolicy({ children, onConfirm, reportName }) {
  const [open, setOpen] = useState(false)

  const handleClick = useCallback((e) => {
    e?.stopPropagation?.()
    setOpen(true)
  }, [])

  const handleConfirm = useCallback(() => {
    setOpen(false)
    auditDownload(reportName)
    onConfirm?.()
  }, [onConfirm, reportName])

  return (
    <>
      {/* Clone child to intercept its onClick */}
      {React.cloneElement(React.Children.only(children), { onClick: handleClick })}
      <PolicyDialogRaw open={open} onClose={() => setOpen(false)} onConfirm={handleConfirm} />
    </>
  )
}

/* ── Hook pattern ──────────────────────────────────────────────── */

export function useDownloadWithPolicy() {
  const [open, setOpen] = useState(false)
  const callbackRef = useRef(null)
  const nameRef = useRef('')

  const triggerDownload = useCallback((downloadFn, reportName) => {
    callbackRef.current = downloadFn
    nameRef.current = reportName || ''
    setOpen(true)
  }, [])

  const handleConfirm = useCallback(() => {
    setOpen(false)
    auditDownload(nameRef.current)
    callbackRef.current?.()
    callbackRef.current = null
  }, [])

  const PolicyDialog = (
    <PolicyDialogRaw open={open} onClose={() => setOpen(false)} onConfirm={handleConfirm} />
  )

  return { triggerDownload, PolicyDialog }
}

/* ── Raw dialog (shared by wrapper + hook) ─────────────────────── */

function PolicyDialogRaw({ open, onClose, onConfirm }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 40, height: 40, borderRadius: '50%',
          bgcolor: palette.warning.lighter,
        }}>
          <WarningAmberIcon sx={{ color: palette.warning.dark, fontSize: 22 }} />
        </Box>
        <Typography variant='h6' sx={{ fontWeight: 700 }}>
          Report Download Policy
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant='body2' sx={{ color: palette.text.secondary, lineHeight: 1.8 }}>
          You are about to download a report that may contain sensitive business data.
        </Typography>

        <Box sx={{ mt: 2, pl: 2, borderLeft: `3px solid ${palette.warning.main}` }}>
          <Typography variant='body2' sx={{ color: palette.text.primary, mb: 0.5, fontWeight: 600 }}>
            By proceeding, you acknowledge that:
          </Typography>
          <Box component='ul' sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5, fontSize: '0.8125rem', color: palette.text.secondary } }}>
            <li>This download is in accordance with the company&apos;s data download policy.</li>
            <li>The downloaded report will not be shared with any external party without proper approval.</li>
            <li>You will handle the data in compliance with applicable data protection regulations.</li>
          </Box>
        </Box>

        <Box sx={{
          mt: 2, p: 1.5, borderRadius: 1,
          bgcolor: palette.error.lighter,
          border: `1px solid ${palette.error.light}`,
        }}>
          <Typography variant='caption' sx={{ fontWeight: 700, color: palette.error.dark }}>
            Unauthorized distribution of this data is strictly prohibited.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          variant='outlined'
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: palette.grey[400],
            color: palette.text.secondary,
            '&:hover': { borderColor: palette.grey[600] },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: palette.primary.main,
            '&:hover': { bgcolor: palette.primary.dark },
          }}
        >
          I Agree &amp; Download
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DownloadWithPolicy
